import { useState, useEffect } from "react";
import {
  Save,
  PlusCircle,
  Trash2,
  X,
  AlertCircle,
  ArrowLeft,
  CheckCircle,
} from "lucide-react";

import { adminUserService } from "@/services/api/admin.user.service";

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, type === "success" ? 5000 : 3000);

    return () => clearTimeout(timer);
  }, [onClose, type]);

  const bgColor = type === "success" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200";
  const textColor = type === "success" ? "text-green-700" : "text-red-700";
  const Icon = type === "success" ? CheckCircle : AlertCircle;

  return (
    <div className={`fixed bottom-4 right-4 p-4 border rounded-md shadow-lg flex items-center gap-2 ${bgColor} ${textColor} animate-fadeIn`}>
      <Icon size={20} />
      <span className="text-sm font-medium">{message}</span>
      <button
        onClick={onClose}
        className="ml-2 text-gray-400 hover:text-gray-600"
      >
        <X size={16} />
      </button>
    </div>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default function CreateFilter({ onFilterCreated, onCancel }) {
  const [filterName, setFilterName] = useState("");
  const [filterType, setFilterType] = useState("");
  const [criteriaName, setCriteriaName] = useState("");
  const [criteriaItems, setCriteriaItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [filterNameError, setFilterNameError] = useState("");
  const [successCreated, setSuccessCreated] = useState(false);

  // Add a new toast
  const addToast = (message, type = "error") => {
    const id = Date.now();
    setToasts((prevToasts) => [...prevToasts, { id, message, type }]);
  };

  // Remove a toast by id
  const removeToast = (id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  // Validate form function
  const validateForm = () => {
    let isValid = true;

    if (!filterName.trim()) {
      setFilterNameError("Filter name is required");
      isValid = false;
    }

    if (!filterType.trim()) {
      addToast("Filter type is required");
      isValid = false;
    }

    if (!criteriaName.trim()) {
      addToast("Criteria name is required");
      isValid = false;
    }

    if (criteriaItems.length === 0) {
      addToast("At least one criteria item is required");
      isValid = false;
    }

    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Clear previous errors
    setFilterNameError("");

    // Validate form
    if (!validateForm()) {
      return;
    }

    const filterData = {
      name: filterName,
      type: filterType,
      criteria: {
        [criteriaName]: criteriaItems,
      },
    };

    setIsLoading(true);

    try {
      // Use the filter service to create the filter
      const response = await adminUserService.createFilter(filterData);

      // Check if the response indicates success
      if (response && response.success) {
        setSuccessCreated(true);
        addToast("Filter created successfully!", "success");
        
        // Keep the form data visible for 2 seconds so user can see success
        setTimeout(() => {
          // Reset form after user has seen the success
          resetForm();
          setSuccessCreated(false);
          
          // Call onFilterCreated callback if provided
          if (onFilterCreated) {
            onFilterCreated();
          }
          
          // Automatically redirect back to filter list after 2 seconds
          setTimeout(() => {
            if (onCancel) {
              onCancel();
            }
          }, 2000);
        }, 2000);
      } else {
        // Handle different error scenarios
        const errorMessage = response?.message || 
                            response?.data?.message || 
                            "Failed to create filter";
        
        // Check if it's a duplicate name error
        if (errorMessage.toLowerCase().includes("already exists") || 
            errorMessage.toLowerCase().includes("duplicate")) {
          setFilterNameError("A filter with this name already exists. Please choose a different name.");
          addToast("A filter with this name already exists. Please choose a different name.");
        } else {
          addToast(errorMessage);
        }
      }
    } catch (error) {
      // Handle network errors or other exceptions
      console.error("Error creating filter:", error);
      
      // Extract error message from different possible locations
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          "An unexpected error occurred while creating the filter";
      
      // Check if it's a duplicate name error
      if (errorMessage.toLowerCase().includes("already exists") || 
          errorMessage.toLowerCase().includes("duplicate") ||
          error.response?.status === 409) {
        setFilterNameError("A filter with this name already exists. Please choose a different name.");
        addToast("A filter with this name already exists. Please choose a different name.");
      } else {
        addToast(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const addCriteriaItem = () => {
    if (!newItem.trim()) return;

    setCriteriaItems([...criteriaItems, newItem]);
    setNewItem("");
  };

  const removeCriteriaItem = (index) => {
    const updatedItems = [...criteriaItems];
    updatedItems.splice(index, 1);
    setCriteriaItems(updatedItems);
  };

  const resetForm = () => {
    setFilterName("");
    setFilterType("");
    setCriteriaName("");
    setCriteriaItems([]);
    setNewItem("");
    setFilterNameError("");
  };

  return (
    <>
      <div className="p-6 max-w-3xl mx-auto">
        {/* Back to List Button */}
        <div className="mb-4">
          <button
            type="button"
            onClick={onCancel}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <ArrowLeft size={18} />
            <span>Back to List</span>
          </button>
        </div>

        {/* Title Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Create Filter</h1>
          <p className="text-gray-600">Add custom filters for your application</p>
        </div>

        {/* Success Message Banner */}
        {successCreated && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md text-green-700 flex items-center gap-2">
            <CheckCircle size={20} />
            <span>Filter created successfully! Redirecting back to list...</span>
          </div>
        )}

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Filter Name */}
            <div>
              <label
                htmlFor="filterName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Filter Name
              </label>
              <input
                type="text"
                id="filterName"
                value={filterName}
                onChange={(e) => {
                  setFilterName(e.target.value);
                  // Clear error when user starts typing
                  if (filterNameError) setFilterNameError("");
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  filterNameError ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="e.g. Price Filter"
                disabled={isLoading || successCreated}
              />
              {filterNameError && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {filterNameError}
                </p>
              )}
            </div>

            {/* Filter Type */}
            <div>
              <label
                htmlFor="filterType"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Filter Type
              </label>
              <input
                type="text"
                id="filterType"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. PRICE"
                disabled={isLoading || successCreated}
              />
            </div>
          </div>

          {/* Criteria Name */}
          <div>
            <label
              htmlFor="criteriaName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Criteria Name
            </label>
            <input
              type="text"
              id="criteriaName"
              value={criteriaName}
              onChange={(e) => setCriteriaName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. options"
              disabled={isLoading || successCreated}
            />
          </div>

          {/* Criteria Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Criteria Items
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Low to High"
                disabled={isLoading || successCreated}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCriteriaItem();
                  }
                }}
              />
              <button
                type="button"
                onClick={addCriteriaItem}
                disabled={isLoading || successCreated}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                <PlusCircle size={16} />
                <span>Add</span>
              </button>
            </div>

            {/* Criteria Items List */}
            <div className="mt-3">
              {criteriaItems.length > 0 ? (
                <div className="border border-gray-200 rounded-md divide-y">
                  {criteriaItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-3"
                    >
                      <span>{item}</span>
                      <button
                        type="button"
                        onClick={() => removeCriteriaItem(index)}
                        disabled={isLoading || successCreated}
                        className="text-red-500 hover:text-red-700 transition-colors disabled:text-red-300 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm italic mt-2">
                  No criteria items added yet
                </p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={resetForm}
              disabled={isLoading || successCreated}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={16} />
              <span>Reset</span>
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || successCreated}
              className={`${
                isLoading || successCreated ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
              } text-white px-6 py-2 rounded-md transition-colors flex items-center gap-1 disabled:cursor-not-allowed`}
            >
              <Save size={16} />
              <span>
                {isLoading 
                  ? "Saving..." 
                  : successCreated 
                    ? "Saved Successfully!" 
                    : "Save Filter"
                }
              </span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
}